import React, { useEffect, useState } from "react";
import moment from "moment";
import { fetchVaultLines } from "../services/matchService";

// "Add a line" (founder decisions 2026-10-08): every typed Vault line between
// the two members, live and removed, and every line the chat text check
// refused. Monitored the same as chat: a removed line keeps its text here
// until the introduction ends, as a removed chat message does in the Chat
// tab. Backend: GET /admin/vault-lines/:userId/:matchedUserId (utils/vaultLine
// adminListForPair), whatever vault_line_v1 says.
export const TIER_LABELS = { iceBreaker: "Ice Breaker", wildCard: "Wild Card" };

const when = (v) => (v ? moment(v).format("DD-MM-YYYY HH:mm") : "N/A");

/** Pure: what the Status column says about one line. */
export const lineStatusOf = (l) => {
  if (l.status === "removed") return `Removed ${when(l.removedAt)}`;
  return l.partnerAnswered ? "Live, the other person can read it" : "Live, sealed until the other person answers";
};

const VaultLinesPanel = ({ userId, matchedUserId, names = {} }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let current = true;
    setData(null);
    setError("");
    fetchVaultLines(userId, matchedUserId)
      .then((res) => {
        if (!current) return;
        if (res?.data?.status === 1) setData(res.data.data);
        else setError(res?.data?.message || "Could not load the Vault lines");
      })
      .catch((err) => {
        if (current) setError(err?.response?.data?.message || err?.message || "Could not load the Vault lines");
      });
    return () => {
      current = false;
    };
  }, [userId, matchedUserId]);

  const lines = data?.lines || [];
  const refusals = data?.refusals || [];
  const nameOf = (id) => names[id] || lines.find((l) => l.authorId === id)?.authorName || "A member";

  return (
    <>
      <label className="form-label fw-bold mb-2">Vault lines</label>
      {error && <p className="text-danger">{error}</p>}
      {!data && !error && <p className="text-muted">Loading the Vault lines.</p>}
      {data && !lines.length && <p className="text-muted">No Vault lines for this pair.</p>}
      {lines.length > 0 && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Author</th>
                    <th>Question</th>
                    <th>Tier</th>
                    <th>Line</th>
                    <th>Status</th>
                    <th>Edits</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={`${l.authorId}-${l.questionId}`}>
                      <td>
                        {l.authorName || nameOf(l.authorId)}
                        {l.authorEmail && (
                          <>
                            <br />
                            <small className="text-muted">{l.authorEmail}</small>
                          </>
                        )}
                      </td>
                      <td>{l.questionText}</td>
                      <td>{TIER_LABELS[l.tier] || l.tier}</td>
                      <td>{l.text}</td>
                      <td>{lineStatusOf(l)}</td>
                      <td>{l.edits || 0}</td>
                      <td>{when(l.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <label className="form-label fw-bold mb-2">Refused lines</label>
      {data && !refusals.length && <p className="text-muted">No refused lines for this pair.</p>}
      {refusals.length > 0 && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>When</th>
                    <th>Author</th>
                    <th>Question</th>
                    <th>Categories</th>
                    <th>Confidence</th>
                    <th>Text</th>
                  </tr>
                </thead>
                <tbody>
                  {refusals.map((r, i) => (
                    <tr key={`${r.at}-${i}`}>
                      <td>{when(r.at)}</td>
                      <td>{nameOf(r.authorId)}</td>
                      <td>{r.questionText}</td>
                      <td>{(r.types || []).join(", ")}</td>
                      <td>{r.confidence || "N/A"}</td>
                      <td>{r.text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VaultLinesPanel;
